
import { type CountryCode, isValidPhoneNumber } from 'libphonenumber-js/mobile'
import { Component, Prop, Vue } from 'vue-property-decorator'
import VueSelect from 'vue-select'

import {
  type CountryOption,
  formatPhoneNumber,
  getAllCountryOptions,
  getDefaultCountry,
  parsePhoneNumber,
} from './helpers'

@Component({
  name: 'PhoneNumberInput',
  components: { VueSelect },
  inheritAttrs: false,
})
export default class extends Vue {
  @Prop({ default: '' }) value!: string

  countryOptions: CountryOption[] = getAllCountryOptions()
  defaultCountry: CountryCode = getDefaultCountry(this.value)
  selectedCountry: CountryCode = this.defaultCountry
  dropdownOpen: boolean = false
  formattedPhoneNumberValue: string = formatPhoneNumber(this.value, this.defaultCountry)

  get input(): string {
    return this.value
  }

  set input(value: string) {
    this.$emit('input', value)
  }

  searchFilterCallback(option: CountryOption, _: string, search: string): boolean {
    // Is searchable by all properties (country code, calling code and country name)
    for (const property in option) {
      if (option[property].toLowerCase().includes(search.toLowerCase())) {
        return true
      }
    }
    return false
  }

  // TODO: fix open dropdown reopening on chevron click
  // Not an issue when using @vueform/multiselect
  handleSearchFocus(): void {
    this.dropdownOpen = true
  }

  clearValidationError(): void {
    ;(this.$refs.phoneNumberInputElement as HTMLInputElement | null)?.setCustomValidity('')
  }

  validatePhoneNumber(): void {
    if (this.input && !isValidPhoneNumber(this.input)) {
      ;(this.$refs.phoneNumberInputElement as HTMLInputElement | null)?.setCustomValidity(
        'Please enter a valid mobile phone number.',
      )
    }
  }

  handleBlur(): void {
    this.$nextTick(() => {
      const isFocusStillWithinComponent =
        this.dropdownOpen || document.activeElement === this.$refs.phoneNumberInputElement
      if (isFocusStillWithinComponent) {
        this.clearValidationError()
      } else {
        this.validatePhoneNumber()
      }
      this.$emit('blur')
    })
  }

  handleSearchBlur(): void {
    this.dropdownOpen = false
    this.handleBlur()
  }

  focusPhoneNumberInput(): void {
    ;(this.$refs.phoneNumberInputElement as HTMLInputElement | null)?.focus()
  }

  updatePhoneNumberValue(updatedNumber: string): void {
    this.clearValidationError()

    const { parsedNumber, updatedCountry } = parsePhoneNumber(
      updatedNumber,
      this.selectedCountry,
      this.defaultCountry,
    )

    this.selectedCountry = updatedCountry
    this.input = parsedNumber
    this.formattedPhoneNumberValue = formatPhoneNumber(updatedNumber, this.selectedCountry)
  }

  handleCountrySelect(): void {
    // Reusing formatted value since the number isn't changing
    // libphonenumber-js doesn't have trouble parsing it
    this.updatePhoneNumberValue(this.formattedPhoneNumberValue)
    this.dropdownOpen = false
    this.focusPhoneNumberInput()
  }

  // Allow reselection of same country (close dropdown and do nothing)
  // VueSelect doesn't have an obvious way to allow selection of currently selected item
  handleCountryReselect(reselectedCountry: CountryCode): void {
    this.selectedCountry = reselectedCountry
    this.dropdownOpen = false
    this.focusPhoneNumberInput()
  }

  handlePhoneNumberInputChange(event: Event): void {
    const updatedNumber = (event.target as HTMLInputElement).value
    this.updatePhoneNumberValue(updatedNumber)
  }
}
